import React, { useState, useRef, useEffect } from 'react';
import { FileText, Sparkles, Download, Check, Copy, Send, Loader2, Lock } from 'lucide-react';
import '../newsite/newsite.css';
import '../newsite/onboard.css';
import { SiteEffects, SiteHeader, usePageMeta } from '../newsite/SiteChrome';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); } catch {
    const el = document.createElement('textarea');
    el.value = text; document.body.appendChild(el); el.select();
    document.execCommand('copy'); document.body.removeChild(el);
  }
};

const CopyBtn = ({ text, label = 'Copy', testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="button button-secondary copy-btn"
      onClick={async () => { await copyText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      data-testid={testId}
    >
      {copied ? <><Check size={13} />&nbsp;Copied</> : <><Copy size={13} />&nbsp;{label}</>}
    </button>
  );
};

const BriefWorkspace = ({ detail, adminKey, onUpdate }) => {
  const [busy, setBusy] = useState('');
  const [chatDraft, setChatDraft] = useState('');
  const [actionError, setActionError] = useState('');
  const chatEndRef = useRef(null);
  const briefChat = detail.brief_chat || [];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [briefChat.length, busy]);

  const call = async (path, opts = {}) => {
    const res = await fetch(`${API}/admin/onboarding/${detail.id}${path}`, {
      ...opts,
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
    return res;
  };

  const generateBrief = async () => {
    setBusy('generate'); setActionError('');
    try {
      const { brief } = await (await call('/generate-brief', { method: 'POST' })).json();
      onUpdate({ ...detail, brief });
    } catch (e) { setActionError(e.message); }
    setBusy('');
  };

  const sendChat = async () => {
    const message = chatDraft.trim();
    if (!message || busy) return;
    setBusy('chat'); setActionError(''); setChatDraft('');
    onUpdate({ ...detail, brief_chat: [...briefChat, { role: 'admin', text: message }] });
    try {
      const { reply, brief } = await (await call('/brief-chat', { method: 'POST', body: JSON.stringify({ message }) })).json();
      onUpdate({
        ...detail,
        brief,
        brief_chat: [...briefChat, { role: 'admin', text: message }, { role: 'assistant', text: reply }],
      });
    } catch (e) { setActionError(e.message); }
    setBusy('');
  };

  const finalize = async () => {
    setBusy('finalize'); setActionError('');
    try {
      const { email_draft } = await (await call('/finalize', { method: 'POST' })).json();
      onUpdate({ ...detail, finalized: true, email_draft });
    } catch (e) { setActionError(e.message); }
    setBusy('');
  };

  const downloadPdf = async () => {
    setBusy('pdf'); setActionError('');
    try {
      const res = await call('/pdf');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `XScale-Project-Brief-${detail.contact.company.replace(/[^A-Za-z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setActionError(e.message); }
    setBusy('');
  };

  const emailTo = `${detail.contact.name} <${detail.contact.email}>`;

  return (
    <div data-testid="brief-workspace">
      {!detail.brief ? (
        <div className="brief-empty" data-testid="brief-empty">
          <p className="onboard-sub" style={{ margin: 0 }}>No brief yet for this conversation.</p>
          <button className="button button-primary" onClick={generateBrief} disabled={!!busy} data-testid="generate-brief-btn">
            {busy === 'generate' ? <><Loader2 size={14} className="animate-spin" />&nbsp;Structuring brief…</> : <><FileText size={14} />&nbsp;Generate project brief</>}
          </button>
        </div>
      ) : (
        <>
          <div className="brief-toolbar">
            <div className="section-label">
              PROJECT BRIEF {detail.finalized && <span className="admin-pill finalized" data-testid="finalized-pill"><Lock size={9} style={{ verticalAlign: '-1px' }} /> Finalized</span>}
            </div>
            <div className="brief-toolbar-actions">
              {!detail.finalized && (
                <>
                  <button className="button button-secondary" onClick={generateBrief} disabled={!!busy} data-testid="regenerate-brief-btn">
                    {busy === 'generate' ? 'Regenerating…' : 'Regenerate'}
                  </button>
                  <button className="button button-primary" onClick={finalize} disabled={!!busy} data-testid="finalize-brief-btn">
                    {busy === 'finalize' ? <><Loader2 size={14} className="animate-spin" />&nbsp;Finalizing…</> : 'Finalize brief'}
                  </button>
                </>
              )}
              {detail.finalized && (
                <button className="button button-primary" onClick={downloadPdf} disabled={!!busy} data-testid="download-pdf-btn">
                  {busy === 'pdf' ? <><Loader2 size={14} className="animate-spin" />&nbsp;Building PDF…</> : <><Download size={14} />&nbsp;Download branded PDF</>}
                </button>
              )}
            </div>
          </div>

          <div className="brief-md" data-testid="admin-brief">{detail.brief}</div>

          {!detail.finalized && (
            <div className="brief-refine" data-testid="brief-refine">
              <div className="section-label" style={{ marginBottom: 12 }}><Sparkles size={11} style={{ verticalAlign: '-1px' }} /> REFINE WITH THE SPECIALIST</div>
              {briefChat.length > 0 && (
                <div className="refine-messages" data-testid="brief-chat-messages">
                  {briefChat.map((m, i) => (
                    <div key={i} className={`refine-msg ${m.role}`}>
                      <b>{m.role === 'admin' ? 'You' : 'Specialist'}</b>{m.text}
                    </div>
                  ))}
                  {busy === 'chat' && <div className="refine-msg assistant"><b>Specialist</b>Reworking the brief…</div>}
                  <div ref={chatEndRef} />
                </div>
              )}
              <div className="composer-bar refine-bar">
                <textarea
                  rows={1}
                  value={chatDraft}
                  placeholder='e.g. "Tighten the executive summary and add a phased rollout to the scope"'
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  disabled={busy === 'chat'}
                  data-testid="brief-chat-input"
                />
                <button className="composer-btn send" onClick={sendChat} disabled={!!busy || !chatDraft.trim()} aria-label="Send tweak" data-testid="brief-chat-send-btn">
                  {busy === 'chat' ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          )}

          {detail.finalized && detail.email_draft && (
            <div className="email-card" data-testid="email-draft-card">
              <div className="section-label" style={{ marginBottom: 16 }}>SUGGESTED CLIENT EMAIL</div>
              <div className="email-row">
                <div className="email-label">To</div>
                <div className="email-value" data-testid="email-to">{emailTo}</div>
                <CopyBtn text={detail.contact.email} label="Copy email" testId="copy-email-btn" />
              </div>
              <div className="email-row">
                <div className="email-label">Subject</div>
                <div className="email-value" data-testid="email-subject">{detail.email_draft.subject}</div>
                <CopyBtn text={detail.email_draft.subject} testId="copy-subject-btn" />
              </div>
              <div className="email-row body">
                <div className="email-label">Body</div>
                <div className="email-value email-body" data-testid="email-body">{detail.email_draft.body}</div>
                <CopyBtn text={detail.email_draft.body} testId="copy-body-btn" />
              </div>
            </div>
          )}
        </>
      )}
      {actionError && <div className="onboard-error" style={{ marginTop: 12 }} data-testid="brief-action-error">{actionError}</div>}
    </div>
  );
};

export default function AdminOnboarding() {
  usePageMeta('Onboarding Admin — X Scale', 'Internal onboarding submissions.');

  const [key, setKey] = useState(sessionStorage.getItem('xs_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState('');
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = async (k) => {
    const res = await fetch(`${API}/admin/onboarding`, { headers: { 'X-Admin-Key': k } });
    if (res.status === 401) { setError('Invalid passcode.'); setAuthed(false); return; }
    const data = await res.json();
    sessionStorage.setItem('xs_admin_key', k);
    setList(data);
    setAuthed(true);
    setError('');
  };

  const openDetail = async (id) => {
    const res = await fetch(`${API}/admin/onboarding/${id}`, { headers: { 'X-Admin-Key': key } });
    if (!res.ok) return;
    setDetail(await res.json());
  };

  const backToList = () => { setDetail(null); load(key); };

  const pill = (s) => {
    if (s.finalized) return <span className="admin-pill finalized">Finalized</span>;
    if (s.status === 'submitted') return <span className="admin-pill submitted">Submitted</span>;
    return <span className="admin-pill">{s.completed ? 'Complete' : 'In progress'}</span>;
  };

  return (
    <div className="xscale-site" data-testid="admin-onboarding-page">
      <SiteEffects />
      <SiteHeader onCasePage />
      <div className="admin-shell">
        <div className="section-label">INTERNAL</div>
        <h1>Onboarding submissions</h1>

        {!authed && (
          <form className="onboard-form admin-gate" onSubmit={(e) => { e.preventDefault(); load(key); }} data-testid="admin-gate-form">
            <div className="onboard-field">
              <label htmlFor="adm-key">Admin passcode</label>
              <input id="adm-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input" />
            </div>
            {error && <div className="onboard-error" data-testid="admin-error">{error}</div>}
            <button type="submit" className="button button-primary" data-testid="admin-login-btn">Unlock</button>
          </form>
        )}

        {authed && !detail && (
          <div className="admin-list" data-testid="admin-session-list">
            {list.length === 0 && <p className="onboard-sub">No onboarding sessions yet.</p>}
            {list.map((s) => (
              <button key={s.id} className="admin-row" onClick={() => openDetail(s.id)} data-testid="admin-session-row">
                <span>
                  <span className="ar-name">{s.contact.name} — {s.contact.company}</span>
                  <span className="ar-meta" style={{ display: 'block' }}>
                    {s.contact.email} · {s.message_count} messages · {new Date(s.updated_at).toLocaleString()}
                  </span>
                </span>
                {pill(s)}
              </button>
            ))}
          </div>
        )}

        {authed && detail && (
          <div data-testid="admin-session-detail">
            <button className="admin-back" onClick={backToList} data-testid="admin-back-btn">← All submissions</button>
            <div className="admin-detail">
              <div className="section-label" style={{ marginBottom: 8 }}>
                {detail.contact.name} · {detail.contact.company} · {detail.contact.email}
              </div>
              <BriefWorkspace detail={detail} adminKey={key} onUpdate={setDetail} />
              <div className="admin-transcript" data-testid="admin-transcript">
                <div className="section-label" style={{ marginBottom: 16 }}>TRANSCRIPT</div>
                {detail.messages.map((m, i) => (
                  <div key={i} className={`t-line ${m.role}`}>
                    <b>{m.role === 'assistant' ? 'X Scale' : m.speaker}</b>{m.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
